import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { Currency } from "./currencies";

export const zCurrency = createEnum(Currency, { enumName: "Currency" });

export const zUsdDollars = z.number().brand<"dollars">();
export type UsdDollars = z.infer<typeof zUsdDollars>;

export const zUsdCents = z.number().brand<"cents">();
export type UsdCents = z.infer<typeof zUsdCents>;

export const zUsdThousandthCents = z.number().brand<"thousandthCents">();
export type UsdThousandthCents = z.infer<typeof zUsdThousandthCents>;

export const zUsdMicros = z.number().brand<"micros">();
export type UsdMicros = z.infer<typeof zUsdMicros>;

type CurrencyAmount = UsdDollars | UsdCents | UsdThousandthCents | UsdMicros;

const DOLLAR_TO_MICROS = 1_000_000;
const CENT_TO_THOUSANDTH_CENTS = 1000;
const DOLLAR_TO_CENTS = 100;

export function dollarsToCents(dollars: UsdDollars): UsdCents {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Math.round(dollars * DOLLAR_TO_CENTS) as UsdCents;
}

export function dollarsToThousandthCents(
  dollars: UsdDollars,
): UsdThousandthCents {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Math.round(
    dollars * DOLLAR_TO_CENTS * CENT_TO_THOUSANDTH_CENTS,
  ) as UsdThousandthCents;
}

export function centsToDollars(cents: UsdCents): UsdDollars {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (Math.round(cents) / DOLLAR_TO_CENTS) as UsdDollars;
}

export function thousandthCentsToDollars(
  thousandthCents: UsdThousandthCents,
): UsdDollars {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (Math.round(thousandthCents) /
    (DOLLAR_TO_CENTS * CENT_TO_THOUSANDTH_CENTS)) as UsdDollars;
}

export function microsToDollars(micros: UsdMicros): UsdDollars {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (Math.round(micros) / DOLLAR_TO_MICROS) as UsdDollars;
}

export function dollarsToMicros(dollars: UsdDollars): UsdMicros {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Math.round(dollars * DOLLAR_TO_MICROS) as UsdMicros;
}

export function thousandthCentsToMicros(
  thousandthCents: UsdThousandthCents,
): UsdMicros {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (Math.round(thousandthCents) * 10) as UsdMicros;
}

export function addMoney<T extends CurrencyAmount>(...amounts: T[]): T {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (amounts as number[]).reduce((acc, amount) => acc + amount, 0) as T;
}

export function multiplyMoney<T extends CurrencyAmount>(
  amount: T,
  factor: number,
): T {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (amount * factor) as T;
}

export function roundMoney<T extends CurrencyAmount>({
  amount,
  decimals,
}: {
  amount: T;
  decimals: number;
}): T {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Number(amount.toFixed(decimals)) as T;
}

export function floorMoney<T extends CurrencyAmount>({
  amount,
  decimals = 0,
}: {
  amount: T;
  decimals?: number;
}): T {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (Math.floor(amount * 10 ** decimals) / 10 ** decimals) as T;
}
